import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createFileRoute } from '@tanstack/react-router';
import { ReworkProducts } from '~/components/common/ai-tools/rework-products/rework-products';
import { UploadCSV } from '~/components/common/ai-tools/upload-csv/upload-csv';
import { useAiProductsUpdatedSubscription } from '~/hooks/use-ai-products-updated-subscription';
import { withAuthorizationRequired } from '~/libs/auth';
import { AppLoader } from '~/routes/_auth';
import { titleWrapper } from '~/utils/title';
import { aitoolsQueryParamsSchema } from '~/validators/aitools-validator';

enum Steps {
  DROPZONE,
  REWORK,
}

export const AI_TOOLS_SCOPES = [
  'scope.meusnidus.iatools.view',
  'scope.meusnidus.iatools.write',
];

export const Route = createFileRoute('/_auth/aitools/')({
  component: withAuthorizationRequired(RouteComponent, {
    onRedirecting: AppLoader,
    requireScope: AI_TOOLS_SCOPES,
  }),
  validateSearch: (search) => aitoolsQueryParamsSchema.parse(search),
});

function RouteComponent() {
  const { t } = useTranslation();

  const sessionIdRef = useRef<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  if (!sessionIdRef.current) {
    sessionIdRef.current = Math.random().toString(36).substring(2);
  }

  const sessionId = sessionIdRef.current;

  useAiProductsUpdatedSubscription({
    sessionId: sessionId,
  });

  const [step, setStep] = useState(Steps.DROPZONE);

  let componentToRender;
  switch (step) {
    case Steps.REWORK:
      componentToRender = (
        <ReworkProducts
          sessionId={sessionId}
          onCancel={() => setStep(Steps.DROPZONE)}
        />
      );
      break;
    case Steps.DROPZONE:
    default:
      componentToRender = (
        <UploadCSV
          onConfirm={() => setStep(Steps.REWORK)}
          onCancel={() => setStep(Steps.DROPZONE)}
        />
      );
  }

  return (
    <>
      <title>{titleWrapper(t('aitools_page.url_title'))}</title>
      {componentToRender}
    </>
  );
}
